/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/naming-convention */
import { IBrandInfo, ICharge, IDiscount, IGroupedItems } from "@swiggy-private/checkout-types";
import { IColors, ITypography } from "@swiggy-private/dls-web-common/dist/components/types.internal";
import { IIconType } from "@swiggy-private/dls-web-core/dist/components/ListView/types";
import { OrderApiData, ApiResponse } from "@swiggy-private/payment-types";
import { STORE_TYPE } from "../constants/EventBooking";
import { VOUCHER_ENTITY_CARD_TYPES } from "../constants/Constants";
import { IBillEntries } from "./BillDetails";
import {
    GuestSplitsMap,
    ISplitBillCommsSuccessData,
    SplitBillConfirmData,
    SplitBillContactsView,
    SplitMethod,
} from "./SplitBill";
import { ITipWidget } from "./TipWidget";

export interface ICompletePaymentCta {
    isApplicable: boolean;
    text: string;
    deeplink: string;
}

export interface IBookingStatusBannerSubtitle {
    text: string;
    textColor: string;
}

interface IBookingStatusBannerTitle {
    text: string;
    textColor: string;
}

export interface IPartialPaymentDetails {
    isPartialPayment: boolean;
    advancePaid: number;
    remainingAmount: number;
}

export type IDineoutPaymentSuccess = ApiResponse<OrderApiData>;

export interface IStoreInfo {
    name: string;
    area: string;
    address: string;
    id: string;
    location: {
        latitude: number;
        longitude: number;
    };
    locality: string;
    landmark: string;
    areaId: number;
    cityId: number;
    maxAllowedCartWeightInGrams: number;
    maxAllowedCartVolumeInCc: number;
    maxAllowedItemsInCart: number;
    minimumOrderValueInRs: number;
    cityName: string;
    displayAreaName: string;
    isPartner: boolean;
    type?: STORE_TYPE; //In case of scenes, an event can be hosted by RX or a non RX entity, this field will be used to identify the type of organiser
}

export interface ICtx {
    background_image: string;
    membership_white_image: string;
    tick_image: string;
    paid_image: string;
    deal_image: string;
    one_image: string;
    GIRF_gradient: string;
    GIRF_black_image: string;
    post_order_screen_message_text_line2: string;
    GIRF_image: string;
    post_order_screen_message_text_line1: string;
    reservation_info: any;
    post_order_screen_message_title: string;
    post_order_screen_message_text_line3: string;
    offerTitle: string;
    offerName: string;
    qrCode: string | null;
    post_order_screen_redeem_message: {
        imageUrl: string;
        backgroundColor: string;
        borderColor: string;
    };
    styles?: StylesObject;
    ticketPresentation?: ITicketPresentation;
    isPartialPaymentApplied?: boolean;
    payCta?: ICompletePaymentCta;
    bookingStatusBannerSubtitle?: IBookingStatusBannerSubtitle;
    bookingStatusBannerTitle?: IBookingStatusBannerTitle;
}

export interface ISuccessBannerDetails {
    totalAmount: string;
    paidAmount: string;
    tip: number;
    subscriptionValue: number;
    backgroundImage: string;
}

export interface ITax {
    type: string;
    value: number;
    totalGST?: number;
}

export interface ITicketPresentation {
    colorCodes?: string[];
    oneTicketLogo?: string;
    oneLiteTicketLogo?: string;
    ticketLogo?: string;
}

export interface ISubscription {
    planId?: string;
    basePrice?: string;
    finalPrice?: string;
    planName?: string;
    planType?: string;
    cta?: {
        link: string;
        text: string;
    };
    displayCta?: boolean;
    name?: string;
}

export interface IBillItem {
    id: string;
    leftText: string;
    rightText: string;
    strikethroughAmount?: string;
    leftSubText?: string;
}
export interface IRedemptionEntry {
    label: string;
    value: number;
    leadingIconUrl?: string;
}

export interface IRedemptionDetails {
    dineCashRedemption?: IRedemptionEntry[];
}

export interface IGetAllBillEntriesProps {
    discountList?: IDiscount[];
    subscription: ISubscription;
    feeObject: any;
    totalBillAmount: number;
    toPayAmount: number;
    tip: string;
    tipTitle?: string;
    tipSubText?: string;
    advanceAmount: number;
    dineCash?: number;
    enteredBillAmount: number;
    isTableReservation: boolean;
    guestCount: number;
    isCart?: boolean;
    redeemable?: boolean;
    billItems?: IBillItem[];
    dealCategory?: string;
    gst?: number;
    partialPaymentDetails?: IPartialPaymentDetails;
}
export interface IItem {
    itemId: string;
    bookingDay: string;
    bookingDate: string;
    bookingSlot: string;
    bookingSlotTime: string;

    savingsLine?: string;
    guestCount: number;

    quantity?: number;
    ticketAttributes?: any;
    itemVariantId: string;

    ticketPresentationInfo: {
        ticketLogo: string;
        colorCodes: string[];
    };
    bookingStatus?: string;
}

export enum SaleMode {
    INR = "INR",
    DINECASH = "DINE_CASH",
}

export interface IBillLineItem {
    label: string;
    value: number;
    leadingIconUrl?: string;
}
export interface IBill {
    type: string;
    calculationStrategy: string;
    totalBill: number;
    charges: ICharge[];
    taxes: ITax[];
    discounts: IDiscount[];
    totalBillAmount: number;
    billAmountWithoutDiscounts: number;
    totalDiscountAmount: number;
    totalDiscountPercentage: number;
    totalDineoutBillAmount: number;
    totalSavings: number;
    totalRestaurantGenericDiscountAmount: number;
    totalRestaurantOneDiscountAmount: number;
    isPostDDROrder: boolean;
    subTotal: number;
    billedItems: any[];
    totalTicketBasePrice: number;
    totalTicketEffectivePrice: number;
    advanceAmount: number;
    enteredBillAmount: number; // its the raw amount with out tip, discounts etc. ie., only the amount eneterd by user to pay bill
    totalRewardsRedeemed?: number;
    partialPaymentDetails?: IPartialPaymentDetails;
    saleMode?: SaleMode;
    billedLineItems?: IBillLineItem[];
}

export interface IMetaData {
    type: string;
    allowDuplicateOrder: boolean;
    storeId: number;
    items: any[];
    groupedItems: IGroupedItems[];
    brandInfo: IBrandInfo[];
    cartId: string;
    helperTexts?: {
        cashbackInfoText?: string;
    };
    storeInfo: IStoreInfo;
    bill: IBill;
    structured: boolean;
    includesTransaction: boolean;
    ctx: ICtx;
    pricingResponse: string;
    ageConsentProvided: boolean;
    vendorVerificationRequired: boolean;
    superData: {
        userStatus: string;
        type: string;
    };
    uomsOrder: boolean;
    businesslineFirstOrder: {
        DINEOUT: boolean;
    };
    subscriptionDetails: any;
    cartType: string;
    bulkOrder: boolean;
    useReusablePackaging: boolean;
    dealCategory: string;
    ticketCtx: any;
    reservationOrderId: string;
    gfbOrder: boolean;
    contactLessDelivery: boolean;
    aggregatedItemsInfo: IAggregatedItemsInfo;
    savingCards: ISavingCards;
    rewardsWidget?: {
        backgroundImage: string;
        title: string;
        logo: string;
    };
    splitPayWidget?: ISplitPayWidget;
    tipWidget?: ITipWidget;
    rewardsPoolWidget?: {
        imageId: string;
    };
    carouselBanners?: ICalloutsBanner;
}
export interface IAggregatedItemsInfo {
    ticketCountTitle: string;
    isAnyTicketSoldOut: boolean;
    ticketTitle: string;
    dateDisplayTitle: string;
    timeDisplayTitle: string;
    eventId?: string;
}
export interface StylesObject {
    restInfoColor?: string;
    billClearedColor?: string;
    totalAmountColor?: string;
    paidByYouTextColor?: string;
    oneBenefitCardBackground?: string;
    oneBenefitCardBackgroundGradients?: string;
    oneCardSavingValueColor?: string;
    bottomTextColor?: string;
    backgroundGradientImage?: string;
}

export interface BottomText {
    bottomText1: string;
    bottomText2: string;
    bottomText3: string;
}

export interface IBookingDetails {
    day: string;
    date: string;
    slotGroupTitle: string;
    time: string;
    noOfGuests: number;
    status: string;
    statusBanner?: string;
}

export interface ITicketAttributes {
    offerDetails: {
        title: string;
        chipSrc: string;
        containerColorCode: string;
    };
    showDownloadCta: boolean;
    tnc: string;
    noteText: string;
    thingsToRemember: string[];
}

export interface IRestaurantInfo {
    restaurantName: string;
    restaurantAddress: string;
    restaurantId?: string;
    restaurantCity?: string;
    restaurantLocality?: string;
}

export interface IPaymentSuccessData {
    groupedItems?: IGroupedItems[];
    brandInfo?: IBrandInfo[];
    restaurantInfo: IRestaurantInfo;
    successBannerInfo: {
        totalAmount: string;
        paidAmount: string;
        tip: number;
        subscriptionValue: number;
        backgroundImage: string;
    };
    oneBenefitsCardInfo: {
        savingsValue: string | 0;
        userStatus: boolean;
        oneLogoStyle: any;
        oneLogoUrl: string;
        successAnimationData: BottomText;
        numericValue: number;
        calloutInfo: CalloutInfo;
    };
    billDetailsInfo: any;
    paidImage: string;
    restaurantId: string;
    dealCategory: string;
    styles?: StylesObject;
    bookingDetails: IBookingDetails;
    ticketAttributes: ITicketAttributes;
    itemsData?: any[];
    cashbackInfoText?: string;
    savingCards?: ISavingCards;
    splitPayWidget?: ISplitPayWidget;
    tipWidget?: ITipWidget;
}

export interface IStealDealPaymentSuccessData {
    groupedItems?: IGroupedItems[];
    brandInfo?: IBrandInfo[];
    billDetailsInfo: IBillEntries[];
    aggregatedItemsInfo?: IAggregatedItemsInfo;
}

export interface IVoucherText {
    info: string;
    color?: string;
    font?: string;
}

export interface IVoucherMediaFile {
    type: string;
    tag?: string;
    name?: string;
    url: string;
}

export interface IVoucherPrice {
    mrp: number;
    offerPrice: number;
}

export interface IVoucherAnalytics {
    screenName?: string;
    context?: string;
    objectValue?: string;
    impressionObjectName?: string;
    clickObjectName?: string;
    extraFields?: Record<string, string>;
    position?: number;
}

export enum CTA_TYPE {
    TYPE_INVALID = "INVALID",
    TYPE_EXTERNAL_LINK = "EXTERNAL",
    TYPE_INTERNAL_LINK = "INTERNAL",
    TYPE_DEEP_LINK = "DEEPLINK",
}

export interface IVoucherCta {
    link: string;
    text?: string;
    type: CTA_TYPE;
    analytics?: IVoucherAnalytics;
}

export interface IVoucherInfoCta {
    link: string;
    type: CTA_TYPE;
    analytics?: IVoucherAnalytics;
}

export interface IVoucherInventoryRule {
    minAllowed: number;
    maxAllowed: number;
    minWarning?: string;
    maxWarning?: string;
}

export interface IVoucherDeal {
    id: string;
    price?: IVoucherPrice;
    rule?: {
        inventoryRule?: IVoucherInventoryRule;
    };
    status?: string;
    button?: {
        type: string;
        cta?: IVoucherCta;
    };
    tnc?: {
        title?: IVoucherText;
        subtitle?: IVoucherText;
    };
    statusComm?: {
        text?: string;
    };
}

export interface IVoucherVendorInfo {
    id: string;
    name?: IVoucherText;
    type?: string;
    rating?: {
        value: string;
        style?: string;
    };
    displayAddress?: IVoucherText;
    cta?: IVoucherInfoCta;
}

export interface IVoucherSkuCard {
    "@type": typeof VOUCHER_ENTITY_CARD_TYPES.SKU;
    info: {
        id: string;
        name?: IVoucherText;
        description?: IVoucherText;
        mediaFiles?: IVoucherMediaFile[];
        deals?: IVoucherDeal[];
        vendorInfo?: IVoucherVendorInfo;
        category?: {
            type?: string;
            vegClassifier?: string;
        };
    };
    analytics?: IVoucherAnalytics;
}

export interface IVoucherEntityCardData {
    imageUrl: string;
    title: string;
    subtitle?: string;
    rating?: string;
    ctaLink?: string;
    ctaType?: CTA_TYPE;
}

export interface IVoucherEntityCard {
    card?: {
        card?: IVoucherSkuCard;
    };
}

export interface IVoucherEntityResponse {
    success?: {
        statusMessage?: string;
        cards?: IVoucherEntityCard[];
        firstOffsetRequest?: boolean;
    };
    failure?: {
        errorCode?: number;
        errorMessage?: string;
    };
}
export interface CalloutInfo {
    imageUrl: string;
    backgroundColor: string;
    borderColor: string;
}
export interface CouponEventProps {
    couponDiscount: string;
    restaurantDiscount: string;
    oneDiscount: string;
    bankOffer: string;
}

export enum DineoutPaymentSuccessFlowType {
    DINECASH_STORE = "DINE_CASH_STORE",
    DINE_CASH_COUPON = "DINE_CASH_COUPON",
}

export interface DoSuccessPageProps {
    orderId?: string;
    flowType?: DineoutPaymentSuccessFlowType;
    dealId?: string;
}

export interface IPadding {
    top: string | number;
    bottom: string | number;
    left: string | number;
    right: string | number;
}
export interface IListItemProp {
    title: string;
    expandable: boolean;
    primaryIcon: IIconType;
    expandedIcon: IIconType;
    titleVariant: keyof ITypography;
    customPadding: IPadding;
    onClick?: VoidFunction;
}

export interface ISavingCards {
    title: string;
    cards: {
        type: string;
        headerImage: string;
        amount: string;
        amountImage: string;
        amountColor: string;
        text: string;
        textColor: keyof IColors;
        nudge: {
            text: string;
        };
        backgroundImage: string;
    }[];
    isScenesFlow?: boolean;
}

export interface ISplitModeOption {
    type: SplitMethod;
    displayText: string;
}

export type ISplitBillDineCashOptions = {
    dineCashAmount: string;
    splitDineCash: boolean;
};

export interface ISplitBillDineCashRowProps {
    dineCashSplit: IDineCashSplit;
    onSelectionChange: (options: ISplitBillDineCashOptions) => void;
    orderId?: string;
    amountPaidByUser?: number;
    numberOfGuest?: number;
    restaurantId?: string;
}

export interface ISplitBillFormProps {
    billAmount: number;
    splitBillContacts: SplitBillContactsView;
    splitModeOptions: ISplitModeOption[];
    dineCashSplit?: IDineCashSplit;
    orderId?: string;
    restaurantId?: string;
    selectedTab: SplitMethod;
    guestSplits: GuestSplitsMap;
    validationError: string;
    isFormValid: boolean;

    onTabChange: (tab: SplitMethod) => void;
    onSplitEqually: () => void;
    onConfirm: () => void;
    onDineCashSelectionChange: (options: ISplitBillDineCashOptions) => void;
    onValidationDismiss: () => void;
    onAddMoreGuests: () => void;

    onPercentageChange: (phoneNumber: string, value: string) => void;
    onAmountChange: (phoneNumber: string, value: string) => void;
    onInputBlur: () => void;
    onShareIncrement: (phoneNumber: string) => void;
    onShareDecrement: (phoneNumber: string) => void;
}

export interface ISplitBillHalfCardProps {
    billAmount: number;
    splitBillContacts: SplitBillContactsView;
    splitModeOptions: ISplitModeOption[];
    dineCashSplit?: IDineCashSplit;
    defaultSplitModeSelection: string;
    onConfirm: (splitData: SplitBillConfirmData, upiId: string) => void;
    handleClose: () => void;
    onAddMoreGuests: (onSuccess?: (newCount: number) => void) => void;
    splitBillLoading?: boolean;
    splitBillSuccess?: boolean;
    splitBillSuccessData?: ISplitBillCommsSuccessData | null;
    splitBillError?: string;
    onRetrySplitBill?: () => void;
    orderId?: string;
    restaurantId?: string;
    onKeyboardStateChange?: (keyboardHeight: number) => void;
}

export interface ISplitPayWidgetStateBase {
    title: string;
    subTitle: string;
    lottieId: string;
    widgetLottieId?: string;
    /** Optional DLS sprite icon id rendered before title when image ids are absent. */
    titleIcon?: string;
    titleIconColor?: keyof IColors | string;
    /** When set, controls whether the widget wrapper is clickable for this state. */
    isClickable?: boolean;
}

export interface ISplitPayWidgetDefaultState extends ISplitPayWidgetStateBase {
    leftImageId: string;
    buttonText: string;
    buttonIcon: string;
}

export interface ISplitPayWidgetSuccessState extends ISplitPayWidgetStateBase {
    rightImageId: string;
}

export type ISplitPayWidgetActiveState = ISplitPayWidgetDefaultState | ISplitPayWidgetSuccessState;

export type ISplitPayWidgetStates = Record<string, ISplitPayWidgetActiveState>;

export interface IDineCashSplit {
    title: string;
    imageId: string;
    splitDineCash: boolean;
    iconId: string;
    infoCallouts: string[];
    dineCashAmount: number;
}

export interface ISplitPayWidget {
    splitModeOptions: ISplitModeOption[];
    defaultSplitModeSelection: SplitMethod | string;
    amount: string;
    userName: string;
    backgroundColor: keyof IColors;
    currentSplitWidgetState: string;
    states: ISplitPayWidgetStates;
    dineCashSplit?: IDineCashSplit;
}

export interface ICalloutsBannerCallouts {
    title: string;
    image: string;
    calloutsLottieId: string;
    description: string;
    ctaText: string;
}

export interface ICalloutsBannerItem {
    id: string;
    itemLottieId: string;
    clickable: boolean;
    callouts: ICalloutsBannerCallouts;
}

export interface ICalloutsBanner {
    titleLottieId: string;
    items: ICalloutsBannerItem[];
}

export interface ICalloutsBannerProps {
    calloutsBanner?: ICalloutsBanner;
}

export interface ICalloutsBannerHalfCardProps {
    callouts?: ICalloutsBannerCallouts;
    onCtaClick: VoidFunction;
}

export interface ICalloutsBannerContentProps {
    item: ICalloutsBannerItem;
    onItemClick: (item: ICalloutsBannerItem) => void;
}

export interface ICalloutsBannerVisibilityState {
    showCalloutsBanner: boolean;
    showDinecashPoolBanner: boolean;
}

export interface IConditionalProps {
    earnedDetails?: {
        backgroundImage: string;
        title: string;
        logo: string;
    };
    ctaSubTextMeta?: {
        title: string;
        titleColor: keyof IColors;
    };
    footerImageSrc?: string;
}
